import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {FormControl, Validators} from '@angular/forms';
import {Subscription} from 'rxjs/Subscription';
import {Observable} from 'rxjs/Observable';
import {AuthService, PasswordRecoveryRequestResponse} from '../../services/auth.service';

const EMPTY = '';
const SIGNIN = '/signin';
const REQUEST_MODE = 'recover';
const RESET_MODE = 'reset';
const TOKEN_PREFIX = 'reset=';
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const LOGIN_PATTERN = /^[a-zA-Z0-9_.-]{1,128}$/;
const MAX_RECOVERY_IDENTIFIER_LENGTH = 254;
const EMAIL_SEPARATOR = '@';
const PASSWORD_PATTERN = /^[!-~]{6,72}$/;
const MIN_PASSWORD = 6;
const FIRST_CHARACTER = 0;
const SPACE_CODE = 32;
const LAST_ASCII_CODE = 128;
const INVALID_STATUS = 400;
const INVALID_LINK = 'PASSWORD_RECOVERY_INVALID';
const UNAVAILABLE = 'PASSWORD_RECOVERY_UNAVAILABLE';
const INVALID_LOGIN = 'PASSWORD_RECOVERY_LOGIN_INVALID';
const INVALID_PASSWORD = 'PASSWORD_RECOVERY_PASSWORD_RULE';
const PASSWORD_MISMATCH = 'INCORRECT_PASSWORD';
const PRESERVE_QUERY = 'preserve';
const CLIPBOARD_TEXT = 'Text';

@Component({
  selector: 'app-change-pass',
  templateUrl: './change-pass.component.html',
  styleUrls: ['./change-pass.component.css']
})
export class ChangePassComponent implements OnInit, OnDestroy {
  public signinError = false;
  public disableBtn = false;
  public remoteAccessError = false;
  public disabledUserError = false;
  public forgotDiv = false;
  public loginOld = EMPTY;
  public passwordNewRepeat = EMPTY;
  public passwordNew = EMPTY;
  public passwordOld = EMPTY;
  public repeatError = false;
  public valid = false;
  public recoveryMode = EMPTY;
  public recoverySent = false;
  public recoveryComplete = false;
  public recoveryError = EMPTY;
  public recoveryEmailHint = EMPTY;
  private resetToken = EMPTY;
  private pending: Subscription;

  constructor(private authService: AuthService, private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    const fragment = this.route.snapshot.fragment || EMPTY;
    if (fragment.indexOf(TOKEN_PREFIX) === FIRST_CHARACTER) {
      this.recoveryMode = RESET_MODE;
      this.resetToken = fragment.substring(TOKEN_PREFIX.length);
      this.recoveryError = TOKEN_PATTERN.test(this.resetToken) ? EMPTY : INVALID_LINK;
      this.router.navigate([], {relativeTo: this.route, fragment: null, replaceUrl: true, queryParamsHandling: PRESERVE_QUERY});
      return;
    }
    if (this.route.snapshot.queryParams.mode === REQUEST_MODE) {
      this.recoveryMode = REQUEST_MODE;
    }
  }

  ngOnDestroy(): void {
    if (this.pending) {
      this.pending.unsubscribe();
    }
    this.resetToken = EMPTY;
  }

  @HostListener('paste', ['$event'])
  checkOnPaste(event: ClipboardEvent): void {
    if (this.recoveryMode) {
      return;
    }
    const text = event.clipboardData.getData(CLIPBOARD_TEXT);
    if (text.split(EMPTY).some(character => !this.isAllowedCode(character.charCodeAt(FIRST_CHARACTER)))) {
      event.preventDefault();
    }
  }

  checkInput(event: KeyboardEvent): boolean {
    return this.isAllowedCode(event.which || event.keyCode);
  }

  private isAllowedCode(code: number): boolean {
    return code <= LAST_ASCII_CODE && code !== SPACE_CODE;
  }

  onChange(): void {
    this.router.navigate([SIGNIN]);
  }

  onForgot(): void {
    this.recoveryMode = REQUEST_MODE;
    this.recoveryError = EMPTY;
    this.recoverySent = false;
    this.recoveryEmailHint = EMPTY;
    this.resetToken = EMPTY;
  }

  changePassword(): void {
    if (this.disableBtn) {
      return;
    }
    this.valid = this.passwordNew.length < MIN_PASSWORD || this.passwordNewRepeat.length < MIN_PASSWORD;
    this.repeatError = this.passwordNew !== this.passwordNewRepeat;
    if (this.valid || this.repeatError) {
      return;
    }
    this.disableBtn = true;
    this.signinError = false;
    this.pending = this.authService.changePass(this.loginOld, this.passwordOld, this.passwordNew)
      .subscribe(() => this.onChange(), () => this.legacyFailure());
  }

  submitRecovery(): void {
    if (this.disableBtn || this.recoveryComplete || this.recoverySent) {
      return;
    }
    this.recoveryEmailHint = EMPTY;
    this.recoveryError = this.validateRecovery();
    if (this.recoveryError) {
      return;
    }
    this.disableBtn = true;
    const operation: Observable<PasswordRecoveryRequestResponse | void> = this.recoveryMode === REQUEST_MODE
      ? this.authService.requestPasswordRecovery(this.loginOld.trim())
      : this.authService.confirmPasswordRecovery(this.resetToken, this.passwordNew);
    this.pending = operation.subscribe(response => this.recoverySucceeded(response), error => this.recoveryFailed(error));
  }

  private validateRecovery(): string {
    if (this.recoveryMode === REQUEST_MODE) {
      const identifier = this.loginOld.trim();
      const validEmail = identifier.indexOf(EMAIL_SEPARATOR) > FIRST_CHARACTER &&
        identifier.length <= MAX_RECOVERY_IDENTIFIER_LENGTH && Validators.email(new FormControl(identifier)) === null;
      return LOGIN_PATTERN.test(identifier) || validEmail ? EMPTY : INVALID_LOGIN;
    }
    if (!TOKEN_PATTERN.test(this.resetToken)) {
      return INVALID_LINK;
    }
    if (!PASSWORD_PATTERN.test(this.passwordNew)) {
      return INVALID_PASSWORD;
    }
    return this.passwordNew === this.passwordNewRepeat ? EMPTY : PASSWORD_MISMATCH;
  }

  private recoverySucceeded(response: PasswordRecoveryRequestResponse | void): void {
    this.disableBtn = false;
    this.recoverySent = this.recoveryMode === REQUEST_MODE;
    this.recoveryEmailHint = this.recoverySent && response && response.emailHint || EMPTY;
    this.recoveryComplete = this.recoveryMode === RESET_MODE;
    this.passwordNew = EMPTY;
    this.passwordNewRepeat = EMPTY;
    if (this.recoveryComplete) {
      this.resetToken = EMPTY;
    }
  }

  private recoveryFailed(error: HttpErrorResponse): void {
    this.disableBtn = false;
    this.recoveryEmailHint = EMPTY;
    this.recoveryError = error.status === INVALID_STATUS ? INVALID_LINK : UNAVAILABLE;
  }

  private legacyFailure(): void {
    this.disableBtn = false;
    this.signinError = true;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/auth/change-pass/change-pass.component.ts